import { Space, SpaceType } from '@tribeplatform/gql-client/types'
import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import { useSpaces } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'

import { convertToStatic } from '../../Views/utils'
import { staticFieldsFactory } from '../config'
import { useShowMoreButton } from '../hooks/useShowMoreButton'
import type { SpacesListBlockView } from '../types'
import { generateGqlFields } from '../utils/generateGqlFields'
import { EmptyState } from './EmptyState'
import { GenericSpacesList } from './GenericSpacesList'
import { LoadingState } from './LoadingState'

interface Props {
  view: SpacesListBlockView
  activeFieldId: string
  preview: boolean
}
export const CollectionSpacesList = ({
  view: {
    collectionId,
    sort,
    reverseSort,
    limit,
    style,
    fields,
    gallerySize,
    showDivider,
    showMore,
  },
  activeFieldId,
  preview,
}: Props) => {
  const staticFields = useStaticIntl(staticFieldsFactory)
  const mergedStaticFields = convertToStatic(fields, staticFields, [])

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSpaces({
      variables: {
        ...(collectionId ? { collectionId } : {}),
        limit,
        reverse: reverseSort ?? true,
        orderBy: sort,
        type: [SpaceType.Group],
      },
      fields: generateGqlFields({ staticFields: mergedStaticFields, style }),
    })

  const { nodes: spaces } = simplifyPaginatedResult(data) as { nodes: Space[] }

  const showMoreButton = useShowMoreButton({
    style,
    gallerySize,
    showMore,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  })

  if (!isLoading && data && spaces && spaces.length === 0) {
    return <EmptyState />
  }

  if (isLoading || !data) {
    return (
      <LoadingState
        count={limit}
        style={style}
        gallerySize={gallerySize}
        showDivider={showDivider}
      />
    )
  }

  return (
    <GenericSpacesList
      style={style}
      spaces={spaces}
      fields={mergedStaticFields}
      gallerySize={gallerySize}
      showDivider={showDivider}
      activeFieldId={activeFieldId}
      showMoreButton={showMoreButton}
      preview={preview}
    />
  )
}
